import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Doughnut } from 'react-chartjs-2';

const BillingDonutChart = ({ total, success, label, formatValue }) => {
  const safeTotal = Number(total || 0);
  const safeSuccess = Number(success || 0);
  const error = Math.max(safeTotal - safeSuccess, 0);

  const [hiddenSlices, setHiddenSlices] = useState({
    error: false,
    success: false,
  });

  const visibleError = hiddenSlices.error ? 0 : error;
  const visibleSuccess = hiddenSlices.success ? 0 : safeSuccess;
  const visibleTotal = visibleError + visibleSuccess;

  const percent = safeTotal > 0 ? (visibleTotal / safeTotal) * 100 : 0;

  const data = useMemo(
    () => ({
      labels: ['Inconsistencia Fiscal', 'Sin Inconsistencias'],
      datasets: [
        {
          data: [visibleError, visibleSuccess],
          backgroundColor: ['#ff0000', '#00c82e'],
          borderWidth: 0,
        },
      ],
    }),
    [visibleError, visibleSuccess],
  );

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    cutoutPercentage: 65,
    legend: {
      display: false,
    },
    tooltips: {
      callbacks: {
        label(tooltipItem, chartData) {
          const value = chartData.datasets[0].data[tooltipItem.index];
          return `${chartData.labels[tooltipItem.index]}: ${formatValue(
            value,
          )}`;
        },
      },
    },
  };

  const toggleSlice = (key) => {
    setHiddenSlices((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  return (
    <div className="w-100 text-center" style={{ maxWidth: 520 }}>
      <div style={{ fontWeight: 600, marginBottom: 20, fontSize: 18 }}>
        {label}
      </div>

      <div style={{ position: 'relative', height: 320 }}>
        <Doughnut data={data} options={options} />

        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            width: '70%',
          }}
        >
          <div style={{ fontWeight: 'bold', fontSize: 22 }}>
            {formatValue(visibleTotal)}
          </div>
          <div style={{ marginTop: 8, fontSize: 16 }}>
            {percent.toFixed(1)}%
          </div>
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 16,
          marginTop: 8,
          flexWrap: 'wrap',
          fontSize: 12,
        }}
      >
        <div
          onClick={() => toggleSlice('error')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') toggleSlice('error');
          }}
          role="button"
          tabIndex={0}
          style={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            userSelect: 'none',
            textDecoration: hiddenSlices.error ? 'line-through' : 'none',
            opacity: hiddenSlices.error ? 0.6 : 1,
          }}
        >
          <span
            style={{
              width: 12,
              height: 12,
              backgroundColor: '#ff0000',
              display: 'inline-block',
              marginRight: 6,
            }}
          />
          Inconsistencia Fiscal
        </div>

        <div
          onClick={() => toggleSlice('success')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') toggleSlice('success');
          }}
          role="button"
          tabIndex={0}
          style={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            userSelect: 'none',
            textDecoration: hiddenSlices.success ? 'line-through' : 'none',
            opacity: hiddenSlices.success ? 0.6 : 1,
          }}
        >
          <span
            style={{
              width: 12,
              height: 12,
              backgroundColor: '#00c82e',
              display: 'inline-block',
              marginRight: 6,
            }}
          />
          Sin Inconsistencias
        </div>
      </div>
    </div>
  );
};

BillingDonutChart.propTypes = {
  total: PropTypes.number.isRequired,
  success: PropTypes.number.isRequired,
  label: PropTypes.string.isRequired,
  formatValue: PropTypes.func.isRequired,
};

export default BillingDonutChart;
